import React, { useCallback, useEffect, useState } from 'react';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '@/features/auth/msalInstance.ts';
import { getScopes } from '@/features/auth/config/authConfig.ts';
import { AuthContext, type AuthContextType } from './auth';
import { initAuthErrorHandler, cleanupAuthErrorHandler } from '@/features/auth/authErrorHandler.ts';
import { setFetchClientTokenGetter } from '@/lib/fetchClient';
import { LOGOUT_EVENT_KEY } from '@/features/auth/constants.ts';

/**
 * Top-level provider.
 *
 * MsalProvider (msal-react v5) handles:
 *   - msalInstance.initialize()
 *   - msalInstance.handleRedirectPromise()
 *   - publishing auth state changes to all child hooks
 *
 * Do NOT call initialize() or handleRedirectPromise() manually.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <MsalProvider instance={msalInstance}>
    <AuthContextProvider>{children}</AuthContextProvider>
  </MsalProvider>
);

const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance: msalInstance, accounts } = useMsal();
  const [error, setError] = useState<string | null>(null);

  // Keep the active account in sync whenever the accounts list changes
  useEffect(() => {
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }, [accounts, msalInstance]);

  const login = useCallback(async () => {
    try {
      setError(null);
      await msalInstance.loginRedirect({
        scopes: getScopes(),
        prompt: 'select_account',
      });
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  }, [msalInstance]);

  const logout = useCallback(async () => {
    try {
      setError(null);

      // Notify other tabs about logout FIRST so they can clean up
      try {
        window.localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString());
      } catch (e) {
        console.error(`Failed to set ${LOGOUT_EVENT_KEY} in localStorage:`, e);
      }

      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.origin,
      });
    } catch (err) {
      console.error('Logout error:', err);
      setError(err instanceof Error ? err.message : 'Logout failed');
    }
  }, [msalInstance]);

  const getAccessToken = useCallback(
    async (forceRefresh: boolean = false): Promise<string | null> => {
      const account = msalInstance.getActiveAccount() ?? accounts[0] ?? null;
      if (!account) {
        console.error('No active account found');
        setError('No active account. Please sign in again.');
        return null;
      }

      const request = {
        scopes: getScopes(),
        account,
        forceRefresh,
        // Do NOT pass redirectUri here — it can break the silent iframe flow.
        // The URI from msalConfig.auth.redirectUri is used automatically.
      };

      try {
        // 1st try: serve from cache / use refresh token transparently
        const response = await msalInstance.acquireTokenSilent(request);
        return response.accessToken;
      } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
          // Consent / MFA / login required — cannot refresh silently.
          // Clear the active account so useIsAuthenticated() returns false
          // immediately and the _app.tsx useEffect drives navigation to /sign-in.
          console.warn('Interactive login required:', silentError.errorCode);
          msalInstance.setActiveAccount(null);
          setError('Your session has expired. Please sign in again.');
          return null;
        }

        // Transient error — force a refresh-token round-trip
        try {
          const response = await msalInstance.acquireTokenSilent({
            ...request,
            forceRefresh: true,
          });
          return response.accessToken;
        } catch (refreshError) {
          console.error('Forced token refresh failed:', refreshError);
          // Also clear the account here — if forced refresh fails the session
          // is genuinely dead and the login screen should appear immediately.
          msalInstance.setActiveAccount(null);
          setError('Your session has expired. Please sign in again.');
          return null;
        }
      }
    },
    [msalInstance, accounts]
  );

  // Register the fetchClient token getter synchronously so the _app.tsx loader
  // permission probe (/api/me) can attach Authorization headers on first render.
  setFetchClientTokenGetter(getAccessToken);

  // Wire up the global auth error handler once stable callbacks are ready.
  // Deliberately excludes `error` from deps — the handler only needs
  // getAccessToken; re-registering on every error change would add/remove
  // the storage listener repeatedly for no benefit.
  useEffect(() => {
    const ctx: AuthContextType = { login, logout, getAccessToken, error: null };
    initAuthErrorHandler(ctx);
    return () => {
      cleanupAuthErrorHandler();
    };
  }, [login, logout, getAccessToken]);

  const value: AuthContextType = { login, logout, getAccessToken, error };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
